'use client';

// ============================================================================
// CrewAhead Maritime Platform - useForm Hook
// ============================================================================
// Wrapper around react-hook-form + Zod that adds API error integration,
// toast notifications, and common form patterns.

import { useCallback, useState, useRef } from 'react';
import {
  useForm as useReactHookForm,
  type UseFormReturn,
  type FieldValues,
  type DefaultValues,
  type SubmitHandler,
  type Path,
  type Resolver,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodType, ZodTypeDef } from 'zod';
import { useUIStore } from '../store/uiStore';
import { CrewAheadApiError } from '../lib/api/client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UseFormOptions<T extends FieldValues> {
  /** Zod schema for validation */
  schema: ZodType<T, ZodTypeDef, unknown>;
  /** Default form values */
  defaultValues?: DefaultValues<T>;
  /** Whether to reset form on successful submit */
  resetOnSuccess?: boolean;
  /** Show success toast on submit */
  showSuccessToast?: boolean;
  /** Success toast message */
  successMessage?: string;
  /** Show error toast on submit failure */
  showErrorToast?: boolean;
}

export interface UseFormHookReturn<T extends FieldValues>
  extends Omit<UseFormReturn<T>, 'handleSubmit'> {
  /** Submit handler that wraps your callback with validation + toasts */
  handleSubmit: (onSubmit: SubmitHandler<T>) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  /** Whether form is submitting */
  isSubmitting: boolean;
  /** Server-side form error (from API) */
  serverError: string | null;
  /** Set server error manually */
  setServerError: (error: string | null) => void;
  /** Clear all errors */
  clearErrors: () => void;
  /** Set field errors from API response */
  setFieldErrors: (errors: Record<string, string>) => void;
  /** Check if form has been modified */
  isDirty: boolean;
  /** Reset form to initial/default values */
  resetForm: () => void;
  /** Fill form with data (e.g., for edit mode) */
  fillForm: (data: Partial<T>) => void;
  /** Check if form has unsaved changes */
  hasUnsavedChanges: boolean;
}

// ─── useForm Hook ────────────────────────────────────────────────────────────

/**
 * Enhanced form hook that wraps react-hook-form with Zod validation,
 * API error integration, toast notifications, and dirty-change tracking.
 *
 * @param options - Form configuration options
 * @returns Extended form controls with submission handling
 *
 * @example
 * ```tsx
 * // Basic usage
 * const form = useForm({
 *   schema: loginSchema,
 *   defaultValues: { email: '', password: '' },
 * });
 *
 * const onSubmit = async (data) => {
 *   await login(data);
 * };
 *
 * return (
 *   <form onSubmit={form.handleSubmit(onSubmit)}>
 *     <input {...form.register('email')} />
 *     {form.formState.errors.email && <span>{String(form.formState.errors.email?.message)}</span>}
 *     <button type="submit" disabled={form.isSubmitting}>
 *       {form.isSubmitting ? 'Logging in...' : 'Login'}
 *     </button>
 *   </form>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // With API error mapping
 * const form = useForm({
 *   schema: registerSchema,
 *   defaultValues: { email: '', password: '', confirmPassword: '' },
 *   showSuccessToast: true,
 *   successMessage: 'Account created successfully!',
 *   showErrorToast: true,
 * });
 *
 * const onSubmit = async (data) => {
 *   try {
 *     await register(data);
 *   } catch (err) {
 *     // Field-level errors from API automatically mapped
 *     if (err instanceof CrewAheadApiError && err.details) {
 *       const fieldErrors: Record<string, string> = {};
 *       Object.entries(err.details).forEach(([field, messages]) => {
 *         fieldErrors[field] = messages[0];
 *       });
 *       form.setFieldErrors(fieldErrors);
 *     }
 *     throw err; // Re-throw so handleSubmit catches it
 *   }
 * };
 * ```
 *
 * @example
 * ```tsx
 * // Edit mode with unsaved changes warning
 * const form = useForm({
 *   schema: profileSchema,
 *   defaultValues: existingProfile ?? {},
 *   resetOnSuccess: true,
 * });
 *
 * // Show confirmation before navigating away
 * useEffect(() => {
 *   const handler = (e: BeforeUnloadEvent) => {
 *     if (form.hasUnsavedChanges) {
 *       e.preventDefault();
 *       e.returnValue = '';
 *     }
 *   };
 *   window.addEventListener('beforeunload', handler);
 *   return () => window.removeEventListener('beforeunload', handler);
 * }, [form.hasUnsavedChanges]);
 * ```
 */
export function useForm<T extends FieldValues>(
  options: UseFormOptions<T>
): UseFormHookReturn<T> {
  const {
    schema,
    defaultValues,
    resetOnSuccess = false,
    showSuccessToast = false,
    successMessage = 'Saved successfully',
    showErrorToast = true,
  } = options;

  const addToast = useUIStore((s) => s.addToast);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const initialValuesRef = useRef<DefaultValues<T> | undefined>(defaultValues);

  const form = useReactHookForm<T>({
    resolver: zodResolver(schema) as Resolver<T>,
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const {
    handleSubmit: rhfHandleSubmit,
    formState: { isDirty },
    reset,
    setError,
    clearErrors: rhfClearErrors,
    setValue,
  } = form;

  /**
   * Wraps the submit handler with loading state, error handling, and toasts.
   */
  const handleSubmit = useCallback(
    (onSubmit: SubmitHandler<T>) => {
      return async (e?: React.BaseSyntheticEvent) => {
        e?.preventDefault();
        setServerError(null);
        setIsSubmitting(true);

        try {
          await rhfHandleSubmit(async (data) => {
            await onSubmit(data);

            if (resetOnSuccess) {
              reset(data as DefaultValues<T>);
            }

            if (showSuccessToast) {
              addToast({
                type: 'success',
                title: successMessage,
                duration: 3000,
              });
            }
          })(e);
        } catch (err) {
          const message =
            err instanceof CrewAheadApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Something went wrong';

          setServerError(message);

          if (showErrorToast) {
            addToast({
              type: 'error',
              title: 'Error',
              message,
              duration: 5000,
            });
          }

          // Re-throw for caller to handle if needed
          throw err;
        } finally {
          setIsSubmitting(false);
        }
      };
    },
    [
      rhfHandleSubmit,
      reset,
      resetOnSuccess,
      showSuccessToast,
      successMessage,
      showErrorToast,
      addToast,
    ]
  );

  /**
   * Map API field errors to form field errors.
   */
  const setFieldErrors = useCallback(
    (errors: Record<string, string>) => {
      Object.entries(errors).forEach(([field, message]) => {
        setError(field as Path<T>, {
          type: 'manual',
          message,
        });
      });
    },
    [setError]
  );

  /**
   * Clear all errors (client + server).
   */
  const clearAllErrors = useCallback(() => {
    setServerError(null);
    rhfClearErrors();
  }, [rhfClearErrors]);

  /**
   * Reset form to initial/default values.
   */
  const resetForm = useCallback(() => {
    setServerError(null);
    rhfClearErrors();
    reset(initialValuesRef.current);
  }, [reset, rhfClearErrors]);

  /**
   * Fill form with data (for edit mode).
   */
  const fillForm = useCallback(
    (data: Partial<T>) => {
      Object.entries(data).forEach(([key, value]) => {
        setValue(key as Path<T>, value as T[Path<T>], {
          shouldDirty: false,
          shouldValidate: false,
          shouldTouch: false,
        });
      });
      // Update initial values so reset goes back to this state
      initialValuesRef.current = { ...initialValuesRef.current, ...data } as DefaultValues<T>;
    },
    [setValue]
  );

  return {
    ...form,
    handleSubmit,
    isSubmitting,
    serverError,
    setServerError,
    clearErrors: clearAllErrors,
    setFieldErrors,
    isDirty,
    resetForm,
    fillForm,
    hasUnsavedChanges: isDirty,
  };
}

// ─── Convenience Hooks ───────────────────────────────────────────────────────

/**
 * Hook for forms in "create" mode — resets on success.
 */
export function useCreateForm<T extends FieldValues>(
  options: Omit<UseFormOptions<T>, 'resetOnSuccess'> & { successMessage?: string }
) {
  return useForm<T>({
    ...options,
    resetOnSuccess: true,
    showSuccessToast: true,
    successMessage: options.successMessage ?? 'Created successfully',
  });
}

/**
 * Hook for forms in "edit" mode — shows success toast without resetting.
 */
export function useEditForm<T extends FieldValues>(
  options: Omit<UseFormOptions<T>, 'resetOnSuccess' | 'showSuccessToast'> & { successMessage?: string }
) {
  return useForm<T>({
    ...options,
    resetOnSuccess: false,
    showSuccessToast: true,
    successMessage: options.successMessage ?? 'Changes saved',
  });
}

/**
 * Hook for forms with server-side validation errors.
 * Provides a helper to map API errors to form fields.
 */
export function useFormWithApiErrors<T extends FieldValues>(
  options: UseFormOptions<T>
) {
  const form = useForm<T>({
    ...options,
    showErrorToast: false, // We'll handle errors via form fields
  });

  /**
   * Maps a CrewAheadApiError to form field errors and server error.
   */
  const mapApiError = useCallback(
    (error: unknown) => {
      if (error instanceof CrewAheadApiError) {
        // Set general server error
        form.setServerError(error.message);

        // Map field-level errors
        if (error.details) {
          const fieldErrors: Record<string, string> = {};
          Object.entries(error.details).forEach(([field, messages]) => {
            fieldErrors[field] = Array.isArray(messages) ? messages[0] : String(messages);
          });
          form.setFieldErrors(fieldErrors);
        }

        return true; // Error was mapped
      }

      // Generic error
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      form.setServerError(message);
      return false;
    },
    [form]
  );

  return { ...form, mapApiError };
}

// ─── Unsaved Changes Warning ─────────────────────────────────────────────────

/**
 * Hook that warns the user before leaving a page with unsaved form changes.
 * Also works with Next.js App Router navigation.
 *
 * @example
 * ```tsx
 * useUnsavedChangesWarning(form.hasUnsavedChanges);
 * ```
 */
export function useUnsavedChangesWarning(hasUnsavedChanges: boolean): void {
  // Browser native beforeunload
  useState(() => {
    if (typeof window === 'undefined') return;

    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  });
}